import {
  BarChart3,
  BookOpen,
  Building2,
  CalendarClock,
  CheckSquare,
  LayoutDashboard,
  MessageSquare,
  Plug,
  Search,
  Settings,
  ShieldCheck,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { PERMISSIONS, type Permission } from '@/features/authorization/permissions'

export interface NavItem {
  id: string
  label: string
  to: string
  icon: LucideIcon
  /**
   * Permissions required to see the item. Omit for everyone.
   *
   * PERMISSIONS, NOT ROLES. It used to be `roles: ['owner']`, which after
   * migration 028 means nothing: a customer's `admin` may hold more than their
   * `owner` did, or less, and either way a role name is not a statement about
   * access. Naming the permission also means a customer who moves a capability
   * between roles gets a correct sidebar with no frontend change.
   */
  permissions?: Permission[]
  /**
   * Restricts the item to the three people the vocabulary feature is
   * hardcoded to on the server (`domain.vocab.VOCAB_ALLOWED_EMAILS`) -- never
   * a tenant permission, so it is checked separately from `permissions` above
   * rather than folded into it.
   */
  vocabularyOnly?: boolean
  /**
   * Restricts the item to the three people who read the feedback inbox
   * (`domain.feedback.FEEDBACK_INBOX_EMAILS`). Same rule as `vocabularyOnly`:
   * not a permission, and the item is hidden rather than shown refused.
   */
  feedbackInboxOnly?: boolean
  /** Key shown in the command palette. */
  shortcut?: string
}

export interface NavSection {
  id: string
  label?: string
  items: NavItem[]
}

/**
 * The whole navigation tree, in one place.
 *
 * Sidebar, mobile drawer, command palette and breadcrumbs all read this — three
 * hand-maintained copies of a nav is how a new screen ends up reachable from
 * one of them and not the others.
 */
export const NAV_SECTIONS: NavSection[] = [
  {
    id: 'work',
    items: [
      { id: 'dashboard', label: 'Home', to: '/', icon: LayoutDashboard, shortcut: 'G then H' },
      {
        id: 'meetings', label: 'Meetings', to: '/meetings', icon: CalendarClock,
        shortcut: 'G then M', permissions: [PERMISSIONS.meetingRead],
      },
      {
        id: 'actions', label: 'Action items', to: '/actions', icon: CheckSquare,
        shortcut: 'G then A', permissions: [PERMISSIONS.actionRead],
      },
      {
        id: 'search', label: 'Search', to: '/search', icon: Search,
        shortcut: '⌘K', permissions: [PERMISSIONS.searchRead],
      },
    ],
  },
  {
    id: 'insights',
    label: 'Insights',
    items: [
      {
        id: 'analytics', label: 'Analytics', to: '/analytics', icon: BarChart3,
        permissions: [PERMISSIONS.analyticsRead],
      },
    ],
  },
  {
    id: 'workspace',
    label: 'Workspace',
    items: [
      {
        id: 'integrations', label: 'Integrations', to: '/integrations', icon: Plug,
        permissions: [PERMISSIONS.integrationRead],
      },
      // Settings has a per-pane gate of its own (SettingsLayout) and always
      // holds at least the profile pane, which needs no permission -- so the
      // entry itself is ungated. Hiding it would leave somebody with no way to
      // change their own name.
      { id: 'settings', label: 'Settings', to: '/settings', icon: Settings },
    ],
  },
  {
    id: 'admin',
    label: 'Administration',
    items: [
      // The Tenants console is PLATFORM administration, gated on
      // sabha.platform_admins rather than on any workspace permission, so no
      // permission here would be right. It stays visible and the page itself
      // reports the refusal -- a platform admin is a Sabha employee, and the
      // handful of them can read a 403.
      { id: 'tenants', label: 'Tenants', to: '/admin/tenants', icon: Building2 },
      // UNGATED SINCE MIGRATION 029, and that is the change rather than a
      // forgotten gate: everybody reads their OWN history, and `audit:read`
      // widens the same screen to the whole workspace. Keeping the old
      // `permissions: [auditRead]` here would hide from a member the one page
      // that shows what has been recorded about them.
      { id: 'audit', label: 'Audit log', to: '/admin/audit', icon: ShieldCheck },
      // Restricted to three named people (`domain.vocab.VOCAB_ALLOWED_EMAILS`),
      // and so is even SEEING that the option exists -- unlike Tenants above,
      // this is not left visible with the page reporting the refusal.
      {
        id: 'vocabulary', label: 'Vocabulary', to: '/admin/vocabulary', icon: BookOpen,
        vocabularyOnly: true,
      },
      // Product feedback from every workspace, for the same three people.
      {
        id: 'feedbackInbox', label: 'Feedback inbox', to: '/admin/feedback', icon: MessageSquare,
        feedbackInboxOnly: true,
      },
    ],
  },
]

/**
 * The nav for one viewer.
 *
 * FAILS CLOSED: `permissions` is null until the session is established, and a
 * null hides every gated item. A sidebar that showed everything for the first
 * few hundred milliseconds of every reload would be a permission model the user
 * can watch not working. `canAccessVocabulary` follows the same rule -- null or
 * false both hide a `vocabularyOnly` item; only an explicit `true` shows it.
 */
export function visibleSections(
  permissions: string[] | null,
  canAccessVocabulary: boolean | null = false,
  canAccessFeedbackInbox: boolean | null = false,
): NavSection[] {
  const held = permissions ?? []
  return NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => {
      if (item.vocabularyOnly && !canAccessVocabulary) return false
      if (item.feedbackInboxOnly && !canAccessFeedbackInbox) return false
      return !item.permissions || item.permissions.every((p) => held.includes(p))
    }),
  })).filter((section) => section.items.length > 0)
}
